import { createAsyncThunk } from '@reduxjs/toolkit'
import { SOMETHING_WHEN_WRONG } from 'constant/errorMessages'
import { callErrorMsg } from 'helpers/errorMsg'
import { getAllLocations } from 'features/Location/services/location'
import {
  GetLocationResponseType,
  ListRouteWavesQueryType,
  RouteWaveConstantsType,
  RouteWaveListItemType,
  RouteWavePaginationType,
  LocationQueryType,
  downloadRouteWaveCsv,
  getLocation,
  getRouteWaveConstants,
  listRouteWaves,
  parseStreamedCsvChunks,
  updateRouteWave,
} from 'features/Midmile/service/routeAndSchedule'
import { getCleanQueryObj } from 'utils/queryParamsURL'
import { downloadFileBlob } from 'utils/downloadFileUrl'

export const SLICE_NAME = 'routeAndSchedule'

export type FetchRouteWavesResultType = {
  items: RouteWaveListItemType[]
  pagination?: RouteWavePaginationType
}

export const fetchRouteAndSchedule = createAsyncThunk<
  FetchRouteWavesResultType,
  undefined,
  RejectValueType
>(`${SLICE_NAME}/fetchRouteAndSchedule`, async (_, { rejectWithValue, getState }) => {
  const {
    routeAndSchedule: { query },
  } = getState() as StoreStateType
  const apiQuery = getCleanQueryObj(query) as ListRouteWavesQueryType

  try {
    const { data } = await listRouteWaves(apiQuery)

    return {
      items: data.data,
      pagination: data.pagination as RouteWavePaginationType | undefined,
    }
  } catch (error) {
    callErrorMsg(error)
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }
})

export const fetchRouteConfig = createAsyncThunk<RouteWaveConstantsType>(
  `${SLICE_NAME}/fetchRouteConfig`,
  async (_, { rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await getRouteWaveConstants()

      return data
    } catch (error) {
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchLocations = createAsyncThunk(
  `${SLICE_NAME}/fetchLocations`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllLocations()
      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchLocation = createAsyncThunk<
  GetLocationResponseType['content'],
  LocationQueryType
>(`${SLICE_NAME}/fetchLocation`, async (params, { rejectWithValue }) => {
  try {
    const {
      data: { content },
    } = await getLocation(params)

    return content
  } catch (error) {
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }
})

export const downloadRouteMaster = createAsyncThunk<void, undefined, RejectValueType>(
  `${SLICE_NAME}/downloadRouteMaster`,
  async (_, { rejectWithValue, getState }) => {
    const {
      routeAndSchedule: {
        exportModal: { selectedLocationID, activeStatus },
      },
    } = getState() as StoreStateType

    if (!selectedLocationID) return rejectWithValue(SOMETHING_WHEN_WRONG)

    try {
      const { data } = await downloadRouteWaveCsv({
        location_id: selectedLocationID,
        active: activeStatus === 'active',
      })
      const csv = parseStreamedCsvChunks(data)
      downloadFileBlob({ data: csv, fileName: 'route-wave.csv', mimeType: 'text/csv' })
      return undefined
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const updateRouteMasterStatus = createAsyncThunk<
  void,
  { id: number; active: boolean },
  RejectValueType
>(`${SLICE_NAME}/updateRouteMasterStatus`, async (params, { rejectWithValue }) => {
  const { id, active } = params
  try {
    await updateRouteWave(id, { active })
    return undefined
  } catch (error) {
    callErrorMsg(error)
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }
})
